import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { DialogService } from '../../../../shared/services/dialog.service';
import { ValidationRulesFacade } from '../../facade/validation-rules.facade';
import {
  ICrossFieldRule,
  ICrossFieldRulePatch,
} from '../../models/validation-rule';
import { LocationRuleEditorModalComponent } from '../../modals/location-rule-editor-modal/location-rule-editor-modal.component';

/**
 * Grouped view of the auto-generated CROSS_FIELD_VALUE_DEPENDENCY rules.
 *
 * Rules are reconstructed into a country -> state tree client-side from
 * `params.source_value` plus the template-pair fingerprint:
 *
 * - country -> state rule: `left_template` = State template, `right_template`
 *   = Country template, `source_value` = country code (USA / CAN / MEX).
 * - state -> county rule: `left_template` = County template, `right_template`
 *   = State template, `source_value` = state name.
 *
 * We don't have the template IDs at hand (we'd need to fetch them), so we
 * infer the country->state vs. state->county distinction by checking whether
 * `source_value` matches a known country code. Hits all three real codes and
 * is robust to template ID renumbering.
 */
interface StateGroup {
  stateName: string;
  stateRule: ICrossFieldRule; // state -> county rule
}

interface CountryGroup {
  countryCode: string;
  countryRule: ICrossFieldRule; // country -> state rule
  states: StateGroup[];
  expanded: boolean;
}

const KNOWN_COUNTRY_CODES = new Set(['USA', 'CAN', 'MEX']);

@Component({
  selector: 'app-location-hierarchy-tab',
  standalone: true,
  imports: [CommonModule, FontAwesomeModule, LocationRuleEditorModalComponent],
  templateUrl: './location-hierarchy-tab.component.html',
})
export class LocationHierarchyTabComponent {
  private facade = inject(ValidationRulesFacade);
  private dialog = inject(DialogService);

  private allValueDeps = this.facade.valueDependencyRulesSignal;
  private expandedCountries = signal<Set<string>>(new Set());

  countryGroups = computed<CountryGroup[]>(() => {
    const countryRules: ICrossFieldRule[] = [];
    const stateRules: ICrossFieldRule[] = [];
    for (const r of this.allValueDeps()) {
      const sv = String((r.params as { source_value?: unknown })?.source_value ?? '');
      if (KNOWN_COUNTRY_CODES.has(sv)) {
        countryRules.push(r);
      } else if (sv) {
        stateRules.push(r);
      }
    }

    // Sort country rules in canonical order: USA, CAN, MEX, then anything else
    // alphabetical.
    const canonical = ['USA', 'CAN', 'MEX'];
    countryRules.sort((a, b) => {
      const ai = canonical.indexOf(String((a.params as { source_value?: unknown }).source_value));
      const bi = canonical.indexOf(String((b.params as { source_value?: unknown }).source_value));
      if (ai === -1 && bi === -1) return 0;
      if (ai === -1) return 1;
      if (bi === -1) return -1;
      return ai - bi;
    });

    // Group state rules by their left_template (county template). We assume a
    // single County template in the system (true for the seeded data).
    // Each country's `allowed_target_values` becomes the source-of-truth
    // ordering for its state list.
    const expanded = this.expandedCountries();
    return countryRules.map<CountryGroup>((cr) => {
      const allowed =
        (cr.params as { allowed_target_values?: string[] | undefined })
          ?.allowed_target_values ?? [];
      const stateRulesByName = new Map<string, ICrossFieldRule>();
      for (const sr of stateRules) {
        const name = String((sr.params as { source_value?: unknown }).source_value);
        // Heuristic: a state-rule is "in" this country if its source_value is
        // one of the country's allowed target values.
        if (allowed.includes(name)) {
          stateRulesByName.set(name, sr);
        }
      }
      const states = allowed
        .map<StateGroup | null>((name) => {
          const sr = stateRulesByName.get(name);
          return sr ? { stateName: name, stateRule: sr } : null;
        })
        .filter((s): s is StateGroup => s !== null);
      return {
        countryCode: String((cr.params as { source_value?: unknown }).source_value),
        countryRule: cr,
        states,
        expanded: expanded.has(
          String((cr.params as { source_value?: unknown }).source_value),
        ),
      };
    });
  });

  totalRulesCount = computed(() => this.allValueDeps().length);

  editorModalId = 'validation-rule-location-editor-modal';
  editingRule = signal<ICrossFieldRule | null>(null);

  // ---- UI handlers ------------------------------------------------

  toggleExpand(code: string): void {
    this.expandedCountries.update((set) => {
      const next = new Set(set);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  }

  toggleActive(rule: ICrossFieldRule): void {
    const changes: ICrossFieldRulePatch = { is_active: !rule.is_active };
    this.facade.patchCrossFieldRule(rule.id, changes);
  }

  openEdit(rule: ICrossFieldRule): void {
    this.editingRule.set(rule);
    this.dialog.open(this.editorModalId);
  }

  onEditorClosed(): void {
    this.editingRule.set(null);
    this.dialog.close(this.editorModalId);
  }

  // ---- helpers ----------------------------------------------------

  /** Comma-trimmed preview of the first few allowed target values. */
  allowedPreview(rule: ICrossFieldRule, max = 5): string {
    const values =
      (rule.params as { allowed_target_values?: string[] | undefined })
        ?.allowed_target_values ?? [];
    if (values.length === 0) return '(none)';
    const head = values.slice(0, max).join(', ');
    return values.length > max ? `${head}, …+${values.length - max}` : head;
  }

  countyCount(rule: ICrossFieldRule): number {
    const values =
      (rule.params as { allowed_target_values?: string[] | undefined })
        ?.allowed_target_values ?? [];
    return values.length;
  }

  trackCountry(_: number, c: CountryGroup): string {
    return c.countryCode;
  }

  trackState(_: number, s: StateGroup): string {
    return s.stateName;
  }
}
